import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type QueryParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

export type HeaderParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  private options(params?: QueryParams, headers?: HeaderParams) {
    const parsedHeaders = {
      ...headers,
      'Content-Type': 'application/json',
    };

    return {
      withCredentials: true,
      headers: new HttpHeaders(parsedHeaders),
      params: params
        ? new HttpParams({
            fromObject: params as Record<
              string,
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>
            >,
          })
        : undefined,
    };
  }

  get<T>(path: string, params?: QueryParams): Observable<T> {
    return this.http.get<T>(`${this.base}/${path}`, this.options(params));
  }

  post<T>(
    path: string,
    body: unknown,
    params?: QueryParams,
    header?: HeaderParams,
  ): Observable<T> {
    return this.http.post<T>(
      `${this.base}/${path}`,
      body,
      this.options(params, header),
    );
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.http.put<T>(`${this.base}/${path}`, body, this.options());
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.http.patch<T>(`${this.base}/${path}`, body, this.options());
  }

  delete<T>(
    path: Parameters<typeof this.http.delete>[0],
    options?: Parameters<typeof this.http.delete>[1],
  ): Observable<T> {
    return this.http.delete<T>(`${this.base}/${path}`, {
      ...options,
      withCredentials: true,
    });
  }

  getErrorMessage(error: unknown): string {
    let errorMessage: string = '';
    if (error === null || error === undefined) return errorMessage;
    if (error instanceof HttpErrorResponse) {
      errorMessage = error.message;
    }
    if (typeof error === 'object' && 'message' in error) {
      errorMessage = JSON.stringify(error.message);
    }
    return errorMessage;
  }
}
